import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { opApi } from "@/lib/operator";
import OperatorHeader from "@/components/OperatorHeader";
import { ChevronLeft, ChevronRight, Plus, CheckCircle2, Loader2, CalendarDays } from "lucide-react";

const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function isoDate(d) { return d.toISOString().slice(0, 10); }

function startOfMonth(y, m) { return new Date(y, m, 1); }
function endOfMonth(y, m)   { return new Date(y, m + 1, 0); }

function buildMonthGrid(year, month) {
  // Returns 6×7 grid of Date objects (Monday-first). Outside-month days included.
  const first = startOfMonth(year, month);
  // Monday = 0
  const dow = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - dow);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

export default function OperatorCalendar() {
  const navigate = useNavigate();
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(isoDate(now));

  useEffect(() => {
    opApi.get("/app/diary?limit=500")
      .then(r => setEntries(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const days = useMemo(() => buildMonthGrid(cursor.y, cursor.m), [cursor]);

  // Group entries by ISO date for quick lookup
  const byDate = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      if (!e.work_date) continue;
      if (!m.has(e.work_date)) m.set(e.work_date, []);
      m.get(e.work_date).push(e);
    }
    return m;
  }, [entries]);

  const selectedEntries = byDate.get(selected) || [];
  const monthName = new Date(cursor.y, cursor.m, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  const move = (delta) => {
    let m = cursor.m + delta;
    let y = cursor.y;
    if (m < 0)   { m = 11; y -= 1; }
    if (m > 11)  { m = 0;  y += 1; }
    setCursor({ y, m });
  };

  const goToday = () => {
    const t = new Date();
    setCursor({ y: t.getFullYear(), m: t.getMonth() });
    setSelected(isoDate(t));
  };

  const onAddForDay = () => {
    navigate(`/app/new?date=${selected}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <OperatorHeader back="/app" title="Calendar" />

      <div className="px-4 py-4 max-w-3xl mx-auto">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => move(-1)} data-testid="cal-prev"
                  className="p-2 rounded-lg bg-white border border-slate-200 hover:border-[#1a1a5c] text-slate-700 hover:text-[#1a1a5c]">
            <ChevronLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <CalendarDays size={18} className="text-[#1a1a5c]" />
            <div className="font-display font-black text-xl text-[#1a1a5c]">{monthName}</div>
          </div>
          <button onClick={() => move(1)} data-testid="cal-next"
                  className="p-2 rounded-lg bg-white border border-slate-200 hover:border-[#1a1a5c] text-slate-700 hover:text-[#1a1a5c]">
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="flex justify-center mb-3">
          <button onClick={goToday} data-testid="cal-today"
                  className="text-xs uppercase tracking-widest text-[#c41e3a] hover:underline font-bold">
            Jump to today
          </button>
        </div>

        {/* Calendar grid */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-7 bg-[#1a1a5c] text-white text-[10px] uppercase tracking-widest font-bold">
            {DOW.map(d => (
              <div key={d} className="text-center py-2">{d}</div>
            ))}
          </div>
          {loading ? (
            <div className="p-8 text-slate-500 flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
          ) : (
            <div className="grid grid-cols-7">
              {days.map((d, i) => {
                const iso = isoDate(d);
                const dayEntries = byDate.get(iso) || [];
                const inMonth = d.getMonth() === cursor.m;
                const isToday = iso === isoDate(new Date());
                const isSelected = iso === selected;
                const hasInvoiced = dayEntries.some(e => e.status === "invoiced");
                return (
                  <button key={i} onClick={() => setSelected(iso)}
                          data-testid={`cal-day-${iso}`}
                          className={`relative min-h-[68px] sm:min-h-[88px] border-b border-r border-slate-100 p-1.5 text-left transition-all
                            ${inMonth ? "bg-white" : "bg-slate-50"}
                            ${isSelected ? "ring-2 ring-[#c41e3a] z-10" : "hover:bg-amber-50"}`}>
                    <div className={`text-xs font-bold ${isToday ? "text-white bg-[#c41e3a] rounded-full w-6 h-6 grid place-items-center" : inMonth ? "text-slate-700" : "text-slate-400"}`}>
                      {d.getDate()}
                    </div>
                    {dayEntries.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        <div className="hidden sm:block">
                          {dayEntries.slice(0, 2).map((e, idx) => (
                            <div key={idx} className={`text-[10px] truncate px-1 py-0.5 rounded
                              ${e.status === "invoiced"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-[#1a1a5c]/10 text-[#1a1a5c]"}`}>
                              {e.client_name || e.task || "—"}
                            </div>
                          ))}
                          {dayEntries.length > 2 && (
                            <div className="text-[10px] text-slate-500">+{dayEntries.length - 2} more</div>
                          )}
                        </div>
                        <div className="sm:hidden flex items-center gap-0.5 absolute bottom-1 left-1">
                          {[...Array(Math.min(dayEntries.length, 4))].map((_, idx) => (
                            <span key={idx} className={`w-1.5 h-1.5 rounded-full ${hasInvoiced ? "bg-emerald-500" : "bg-[#c41e3a]"}`} />
                          ))}
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected day panel */}
        <div className="mt-5 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="bg-[#1a1a5c] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/70">Selected</div>
              <div className="font-display font-black">
                {new Date(selected + "T00:00:00").toLocaleDateString("en-GB", {
                  weekday: "long", day: "numeric", month: "long", year: "numeric"
                })}
              </div>
            </div>
            <button onClick={onAddForDay} data-testid="cal-add-for-day"
                    className="bg-[#c41e3a] hover:bg-[#d92a47] text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1.5 active:scale-95 transition-all">
              <Plus size={16} /> Add entry
            </button>
          </div>

          {selectedEntries.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <div className="text-sm">No entries logged on this day yet.</div>
              <button onClick={onAddForDay}
                      className="inline-flex items-center gap-1 text-[#c41e3a] hover:underline font-bold mt-2 text-sm">
                <Plus size={14} /> Log work for this day
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {selectedEntries.map(e => (
                <li key={e.id}>
                  <Link to={e.status === "invoiced" ? "#" : `/app/diary/${e.id}`}
                        data-testid={`cal-entry-${e.id}`}
                        className={`flex items-center gap-3 px-4 py-3 ${e.status === "invoiced" ? "cursor-default" : "hover:bg-slate-50 active:bg-slate-100"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800 truncate">{e.client_name || "—"}</span>
                        {e.status === "invoiced" && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                            <CheckCircle2 size={10} /> {e.invoice_number || "Invoiced"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {e.task || "—"}{e.field_name ? ` · ${e.field_name}` : ""}{e.machine_label ? ` · ${e.machine_label}` : ""}
                      </div>
                      <div className="flex gap-3 mt-1 text-xs">
                        {e.acres != null && <span className="text-slate-600"><span className="text-slate-400">Acres</span> <strong className="text-[#c41e3a]">{e.acres}</strong></span>}
                        {e.hours != null && <span className="text-slate-600"><span className="text-slate-400">Hrs</span> <strong className="text-[#c41e3a]">{e.hours}</strong></span>}
                        {e.fuel_litres != null && <span className="text-slate-600"><span className="text-slate-400">Fuel</span> <strong className="text-[#c41e3a]">{e.fuel_litres}L</strong></span>}
                      </div>
                    </div>
                    <div className="text-xs text-slate-400">{e.operator_name}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
